import { useQuery } from '@tanstack/react-query';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workspaceApi, type SearchItem } from '../../api';
import { useDebounced } from '../../hooks/useDebounced';
import { t, tk } from '../../i18n';
import { cx } from '../../lib/cx';
import { useAuth } from '../../stores/auth';
import { IconSearch } from '../ui/Icon';
import { canSee, navFor, type Section } from './nav';

interface Entry {
  key: string;
  label: string;
  hint: string;
  to: string;
}

/** Куди веде результат пошуку. Контакт окремої картки не має — відкриваємо справи з пошуком за ним. */
export function searchTarget(it: SearchItem): string {
  switch (it.type) {
    case 'case':
      return `/cases/${it.id}`;
    case 'order':
      return `/orders/${it.id}`;
    case 'product':
      return `/products/${it.id}`;
    case 'vehicle':
      return `/fleet?vehicle=${it.id}`;
    case 'trip':
      return `/fleet?trip=${it.id}`;
    case 'contact':
      // справи контакту можуть бути будь-якої послуги — шукаємо по всіх
      return `/cases?q=${encodeURIComponent(it.title ?? '')}&service=all`;
    default:
      return '/';
  }
}

/** Розділ, куди веде результат пошуку: результати недоступних ролі розділів не показуємо. */
export function sectionFor(type: string): Section | null {
  switch (type) {
    case 'case':
    case 'contact':
      return 'cases';
    case 'order':
      return 'orders';
    case 'product':
      return 'catalog';
    case 'vehicle':
    case 'trip':
      return 'fleet';
    default:
      return null;
  }
}

/** Підпис результату: тип і деталь; статус замовлення — людською мовою, а не кодом. */
export function searchHint(it: SearchItem): string {
  const sub = it.type === 'order' ? tk('orderStatus', it.subtitle, it.subtitle ?? '') : it.subtitle;
  return [tk('search.type', it.type), sub].filter(Boolean).join(' · ');
}

/** Результати пошуку, які роль може відкрити. */
export function visibleResults(items: SearchItem[], role: Parameters<typeof canSee>[0]): SearchItem[] {
  return items.filter((it) => {
    const sec = sectionFor(it.type);
    return sec ? canSee(role, sec) : false;
  });
}

/** Ctrl+K: пошук по телефону, номеру справи чи замовлення, ПІБ, держномеру + перехід у розділ. */
export function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const role = useAuth((s) => s.me?.role);
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [cur, setCur] = useState(0);
  const debounced = useDebounced(q.trim(), 200);
  const input = useRef<HTMLInputElement>(null);
  const listId = useId();

  useEffect(() => {
    if (open) {
      setQ('');
      setCur(0);
      setTimeout(() => input.current?.focus(), 0);
    }
  }, [open]);

  const res = useQuery({
    queryKey: ['search', debounced],
    queryFn: ({ signal }) => workspaceApi.search(debounced, signal),
    enabled: open && debounced.length >= 2,
    staleTime: 10_000,
  });

  const entries: Entry[] = useMemo(() => {
    const needle = q.trim().toLowerCase();
    const sections = navFor(role)
      .flatMap((g) => g.items.map((it) => ({ g, it })))
      .filter(({ it }) => !needle || t(it.key).toLowerCase().includes(needle))
      .slice(0, needle ? 4 : 8)
      .map(({ g, it }) => ({ key: `nav:${it.to}`, label: t(it.key), hint: t(g.key), to: it.to }));
    const found = visibleResults(res.data?.items ?? [], role).map((it) => ({
      key: `${it.type}:${it.id}`,
      label: it.title ?? '—',
      hint: searchHint(it),
      to: searchTarget(it),
    }));
    return [...found, ...sections];
  }, [q, res.data, role]);

  useEffect(() => setCur(0), [entries.length]);

  if (!open) return null;
  const go = (e: Entry | undefined) => {
    if (!e) return;
    onClose();
    navigate(e.to);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center bg-black/40 px-4 pt-[12vh]" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div role="dialog" aria-modal="true" aria-label={t('search.title')} className="w-full max-w-[560px] bg-bg border border-line rounded-2xl shadow-2xl overflow-hidden">
        <label className="flex items-center gap-3 px-4 h-14 border-b border-line">
          <IconSearch size={18} className="text-mute" />
          <input
            ref={input}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') onClose();
              else if (e.key === 'ArrowDown') {
                e.preventDefault();
                setCur((c) => Math.min(entries.length - 1, c + 1));
              } else if (e.key === 'ArrowUp') {
                e.preventDefault();
                setCur((c) => Math.max(0, c - 1));
              } else if (e.key === 'Enter') {
                e.preventDefault();
                go(entries[cur]);
              }
            }}
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={entries[cur] ? `${listId}-${cur}` : undefined}
            aria-label={t('search.title')}
            placeholder={t('search.placeholder')}
            className="flex-1 min-w-0 bg-transparent border-0 outline-none text-md text-fg placeholder:text-mute"
          />
          <kbd className="font-sans text-xs text-mute">Esc</kbd>
        </label>
        <ul id={listId} role="listbox" className="max-h-[50vh] overflow-y-auto py-2 m-0 list-none px-2">
          {entries.map((e, i) => (
            <li
              key={e.key}
              id={`${listId}-${i}`}
              role="option"
              aria-selected={i === cur}
              onMouseEnter={() => setCur(i)}
              onMouseDown={(ev) => {
                ev.preventDefault();
                go(e);
              }}
              className={cx('flex items-center justify-between gap-3 px-3 py-2.5 rounded-lg cursor-pointer', i === cur && 'bg-hov')}
            >
              <span className="font-semibold truncate">{e.label}</span>
              <span className="text-xs text-mute truncate">{e.hint}</span>
            </li>
          ))}
          {debounced.length >= 2 && res.isFetched && !visibleResults(res.data?.items ?? [], role).length && (
            <li className="px-3 py-2.5 text-sm text-mute">{t('common.nothingFound')}</li>
          )}
        </ul>
      </div>
    </div>
  );
}

/** Глобальна гаряча клавіша Ctrl/⌘+K. */
export function useCommandHotkey(onOpen: () => void) {
  useEffect(() => {
    const h = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        onOpen();
      }
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onOpen]);
}
